import { sourceKindFromKey, type Aspect, type Resolution } from './api'
import { PROGRAM_FPS } from './constants'
import type { Canvas, LayoutId } from './layouts'

/**
 * The editor shows a recording as its takes laid end to end. "Timeline time" is ms from the start of
 * take 1 with the paused gaps removed.
 */
export interface TakeSpan {
  takeId: string
  index: number
  start: number
  duration: number
}

export function buildTakeSpans(takes: { id: string; index: number; durationMs: number }[]): TakeSpan[] {
  let start = 0
  return [...takes]
    .sort((a, b) => a.index - b.index)
    .map((t) => {
      const span = { takeId: t.id, index: t.index, start, duration: Math.max(0, t.durationMs) }
      start += span.duration
      return span
    })
}

export function timelineDuration(spans: TakeSpan[]): number {
  const last = spans[spans.length - 1]
  return last ? last.start + last.duration : 0
}

export function locateTake(spans: TakeSpan[], t: number): { span: TakeSpan; offset: number } | null {
  for (const span of spans) {
    if (t >= span.start && t < span.start + span.duration) return { span, offset: t - span.start }
  }
  const last = spans[spans.length - 1]
  if (last && t >= last.start + last.duration) return { span: last, offset: last.duration }
  return null
}

/**
 * Moves `t` onto the frame grid of its take. The renderer cuts on that grid, so an edit placed
 * between two frames would land on a different frame than the preview showed.
 */
export function snapToFrame(spans: TakeSpan[], t: number): number {
  const hit = locateTake(spans, t)
  if (!hit) return Math.max(0, Math.round(t))
  const frame = 1000 / PROGRAM_FPS
  const offset = Math.min(hit.span.duration, Math.round(Math.round(hit.offset / frame) * frame))
  return hit.span.start + offset
}

/**
 * Who is heard in one take: every person's voice (the lossless mic, else the sound of their camera)
 * and every screen that carries sound. The editor preview and the renderer both pick with this.
 */
export function audibleMedia<T extends { participantId: string; sourceKey: string }>(inTake: T[], hasSound: (m: T) => boolean): T[] {
  const result: T[] = []
  for (const pid of new Set(inTake.map((m) => m.participantId))) {
    const mine = inTake.filter((m) => m.participantId === pid && hasSound(m))
    const voice = mine.find((m) => m.sourceKey === 'mic') ?? mine.find((m) => m.sourceKey === 'camera')
    if (voice) result.push(voice)
    result.push(...mine.filter((m) => m.sourceKey === 'mix' || sourceKindFromKey(m.sourceKey) === 'screen'))
  }
  return result
}

/** A program boundary: from `t` until the next boundary the program shows `layout` with `slots`. */
export interface Boundary {
  id: string
  t: number
  layout: LayoutId
  /** Video source ids in slot order; slot 0 is the primary. */
  slots: string[]
  /** The range is removed from the output. */
  cut: boolean
  /** Take starts: cannot be moved or deleted. */
  locked: boolean
}

export interface ProgramSegment {
  boundaryId: string
  start: number
  end: number
  layout: LayoutId
  slots: string[]
  cut: boolean
  takeId: string
  /** Offset of `start` within its take. */
  takeOffset: number
}

export interface ProjectMarker {
  id: string
  t: number
  label: string
  color: string
  author: string
}

export interface AudioSetting {
  gainDb: number
  muted: boolean
}

export interface ProjectSnapshot {
  aspect: Aspect
  boundaries: Boundary[]
  markers: ProjectMarker[]
  audio: Record<string, AudioSetting>
}

export const DEFAULT_AUDIO: AudioSetting = { gainDb: 0, muted: false }

/**
 * Turns boundaries into contiguous segments covering the whole timeline. Segments are additionally
 * split at take starts, so every segment lies inside exactly one take — the renderer relies on that.
 */
export function programSegments(boundaries: Boundary[], spans: TakeSpan[]): ProgramSegment[] {
  const total = timelineDuration(spans)
  if (total <= 0) return []
  const sorted = [...boundaries].filter((b) => b.t >= 0 && b.t < total).sort((a, b) => a.t - b.t || a.id.localeCompare(b.id))
  if (sorted.length === 0 || sorted[0]!.t > 0) {
    sorted.unshift({ id: 'implicit-start', t: 0, layout: 'grid', slots: [], cut: false, locked: true })
  }
  const segments: ProgramSegment[] = []
  sorted.forEach((b, i) => {
    const end = sorted[i + 1]?.t ?? total
    if (end <= b.t) return
    for (const span of spans) {
      const s = Math.max(b.t, span.start)
      const e = Math.min(end, span.start + span.duration)
      if (e <= s) continue
      segments.push({
        boundaryId: b.id,
        start: s,
        end: e,
        layout: b.layout,
        slots: b.slots,
        cut: b.cut,
        takeId: span.takeId,
        takeOffset: s - span.start,
      })
    }
  })
  return segments
}

export function segmentAt(segments: ProgramSegment[], t: number): ProgramSegment | null {
  for (const s of segments) if (t >= s.start && t < s.end) return s
  return segments[segments.length - 1] ?? null
}

export function outputDuration(segments: ProgramSegment[]): number {
  return segments.reduce((sum, s) => (s.cut ? sum : sum + (s.end - s.start)), 0)
}

/** Next playable position at or after `t`, skipping cut segments; null at the end. */
export function nextPlayable(segments: ProgramSegment[], t: number): number | null {
  for (const s of segments) {
    if (s.end <= t) continue
    if (!s.cut) return Math.max(t, s.start)
  }
  return null
}

export function canvasFor(aspect: Aspect, resolution: Resolution): Canvas {
  const short = resolution === '720p' ? 720 : resolution === '1080p' ? 1080 : 2160
  if (aspect === '1:1') return { w: short, h: short }
  const long = Math.round((short * 16) / 9 / 2) * 2
  return aspect === '16:9' ? { w: long, h: short } : { w: short, h: long }
}

export interface RenderPlanSegment {
  takeId: string
  from: number
  duration: number
  layout: LayoutId
  slots: string[]
}

export interface RenderPlan {
  canvas: Canvas
  aspect: Aspect
  resolution: Resolution
  segments: RenderPlanSegment[]
  audio: Record<string, AudioSetting>
}

/** Shorter pieces are dropped: below one frame they only produce encoder glitches. */
const MIN_RENDER_SEGMENT_MS = 40

export function buildRenderPlan(project: ProjectSnapshot, spans: TakeSpan[], resolution: Resolution): RenderPlan {
  const segments: RenderPlanSegment[] = []
  for (const s of programSegments(project.boundaries, spans)) {
    if (s.cut || s.end - s.start < MIN_RENDER_SEGMENT_MS) continue
    const prev = segments[segments.length - 1]
    // a split without a change in between would otherwise cost an extra encode and a seam
    if (
      prev &&
      prev.takeId === s.takeId &&
      prev.from + prev.duration === s.takeOffset &&
      prev.layout === s.layout &&
      prev.slots.join() === s.slots.join()
    ) {
      prev.duration += s.end - s.start
      continue
    }
    segments.push({ takeId: s.takeId, from: s.takeOffset, duration: s.end - s.start, layout: s.layout, slots: [...s.slots] })
  }
  return {
    canvas: canvasFor(project.aspect, resolution),
    aspect: project.aspect,
    resolution,
    segments,
    audio: project.audio,
  }
}
